import {AfterViewInit, Component, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, NgForm} from "@angular/forms";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {interval, ReplaySubject} from "rxjs";
import {UserPermissions} from "src/app/shared/interface/user-permissions";
import {CountedAndPaginated} from "src/app/shared/interface/response/counted-and-paginated";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AuthService} from "src/app/shared/services/auth.service";
import {FilterService} from "src/app/shared/services/filter.service";
import {ActivatedRoute, Router} from "@angular/router";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "src/app/shared/ngrx/reducers";
import {FormHelperService} from "src/app/shared/services/form-helper.service";
import {ReceivablesImportsService} from "src/app/shared/services/receivables-imports.service";
import {delay, distinctUntilChanged, takeUntil, tap} from "rxjs/operators";
import {getCurrentRouteState} from "src/app/shared/ngrx/selector";
import {RouterStateUrl} from "src/app/shared/ngrx/route-serializer";
import {HiperioCredentialsJob} from "../../../shared/interface/hiperio-credentials-job";
import {HiperioCredentialsJobsService} from "../../../shared/services/hiperio-credentials-jobs.service";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {
    COMMISSION_JOB_STATUS_IN_SUCCESS,
    FILE_TYPE_ALLIANZ_CERT, FILE_TYPE_ALLIANZ_CERT_DOCUMENTS,
    FILE_TYPE_ALLIANZ_CERT_FRAUDS, FILE_TYPE_ALLIANZ_CERT_MY_CAR
} from "../../../shared/services/dict.service";
import {RiskDictionary} from "../../../shared/interface/risk-dictionary";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";


@Component({
  selector: 'app-credentials-jobs-list',
  templateUrl: './credentials-jobs-list.component.html',
  styleUrls: ['./credentials-jobs-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class CredentialsJobsListComponent implements AfterViewInit, OnInit, OnDestroy  {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  readonly COMMISSION_JOB_STATUS_IN_SUCCESS = COMMISSION_JOB_STATUS_IN_SUCCESS;
  readonly FILE_TYPE_ALLIANZ_CERT = FILE_TYPE_ALLIANZ_CERT;
  readonly FILE_TYPE_ALLIANZ_CERT_FRAUDS = FILE_TYPE_ALLIANZ_CERT_FRAUDS;
  readonly FILE_TYPE_ALLIANZ_CERT_DOCUMENTS = FILE_TYPE_ALLIANZ_CERT_DOCUMENTS;
  readonly FILE_TYPE_ALLIANZ_CERT_MY_CAR = FILE_TYPE_ALLIANZ_CERT_MY_CAR;

  initialFilters: any;
  jobs: HiperioCredentialsJob[] = [];
  displayedColumns: string[] = ['type', 'ofwca', 'registration' , 'createdAt', 'createdBy', 'status', 'action'];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  actionForm: FormGroup;
  expandedElement: HiperioCredentialsJob = null;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);


  constructor(
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
    private fb: FormBuilder,
    private formHelper: FormHelperService,
    private hiperioCredentialsJobsService: HiperioCredentialsJobsService,
    private receivablesImportService: ReceivablesImportsService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.hiperioCredentialsJobsService.jobListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<HiperioCredentialsJob>) => {
          this.jobs = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      type: 'int',
      gte$createdAt: 'date',
      lte$createdAt: 'date',
      partner: 'string',
      ofwca: 'string',
      status: 'int',
      assetsRegional: 'string',
    }, filters);
  }

  ngOnInit(): void {
    interval(5000)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.paginator.pageIndex === 0 && !this.expandedElement) {
          this.loadListData();
        }
      });
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.buildFilters()
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;

        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    return filters;
  }

  loadListData(): void {
    this.hiperioCredentialsJobsService.loadJobList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  restart(uuid: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz uruchomić tę akcję?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.hiperioCredentialsJobsService.restart(uuid)
          .subscribe(() => {
            this.loadListData();
          }, (error: HttpErrorResponse) => {
            this.snack.open('Blokada uruchomienia akcji', error.status.toString(), {duration: 2000});
            if (error.status === 400) {
              this.snack.open(error.error.error.text, '400', {duration: 4000});
            }
          });
      }
    });
  }

  expandElement(hiperioCredentialsJob: HiperioCredentialsJob): void {
    this.expandedElement = this.expandedElement === hiperioCredentialsJob ? null : hiperioCredentialsJob;
  }

  parseNote(note: string): any {
    try {
      return JSON.parse(note);
    } catch (e) {
      return {};
    }
  }

  downloadReport(): void {
    this.hiperioCredentialsJobsService.downloadReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

}
